import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type Stripe from "stripe";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  type StripeEnv,
  createStripeClient,
  getStripeErrorMessage,
} from "@/lib/stripe.server";
import {
  assertAdmin,
  publicClient,
  resolveOrCreateCustomer,
  shopPriceIdFromSlug,
} from "@/lib/shop.server";
import { MEMBERSHIP_PRICE_IDS, MEMBERSHIP_CADENCE } from "@/lib/membership";

const StripeEnvSchema = z.enum(["sandbox", "live"]);

// Consent flags required at every checkout. Subscription checkouts additionally
// require consent_subscription. Timestamps get persisted onto the resulting
// order/subscription row by the webhook using metadata we set here.
type Consent = { terms: true; privacy: true; refund: true; subscription?: true };
const ConsentSchema = z.object({
  terms: z.literal(true),
  privacy: z.literal(true),
  refund: z.literal(true),
});
const SubscriptionConsentSchema = ConsentSchema.extend({
  subscription: z.literal(true),
});

// ---------------- Public catalog ----------------

export const listShopProducts = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("shop_products")
    .select("slug, title, subtitle, long_description, image_url, kind, amount_cents, currency, external_purchase_url, sort_order, requires_shipping")
    .eq("active", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const getShopProduct = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(200) }).parse(input))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("shop_products")
      .select("slug, title, subtitle, long_description, image_url, kind, amount_cents, member_amount_cents, currency, external_purchase_url, sort_order, requires_shipping, shipping_amount_cents, downloadable, streamable, included_with_membership, bundle_items, active")
      .eq("slug", data.slug)
      .eq("active", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row;
  });

// ---------------- Checkout (auth required) ----------------

type CheckoutResult = { clientSecret: string } | { error: string };
type PortalResult = { url: string } | { error: string };

function stampConsentMetadata(consent: Consent) {
  const iso = new Date().toISOString();
  return {
    consent_terms_at: iso,
    consent_privacy_at: iso,
    consent_refund_at: iso,
    ...(consent.subscription ? { consent_subscription_at: iso } : {}),
  };
}

export const createShopCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        slug: z.string().min(1).max(200),
        returnUrl: z.string().url(),
        environment: StripeEnvSchema,
        consent: ConsentSchema,
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<CheckoutResult> => {
    try {
      const { userId, claims, supabase: userSupabase } = context;
      const email = (claims as { email?: string } | undefined)?.email;

      const { data: product, error } = await userSupabase
        .from("shop_products")
        .select("slug, title, subtitle, image_url, amount_cents, currency, active, external_purchase_url, requires_shipping, shipping_amount_cents, tax_code, kind, member_amount_cents")
        .eq("slug", data.slug)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!product || !product.active) throw new Error("Product not available");
      if (product.external_purchase_url) throw new Error("This product is sold on an external site");
      if (!product.amount_cents || product.amount_cents < 50) throw new Error("Product price is not configured");

      // Never let a buyer pay twice for access they already hold.
      const { loadOwnership } = await import("@/lib/ownership.server");
      const { resolvePrice } = await import("@/lib/ownership");
      const ownership = await loadOwnership(userSupabase, userId, data.environment);
      const existing = ownership.bySlug[product.slug];
      if (existing && existing.source !== "membership") {
        throw new Error("You already own this. It is in your library.");
      }

      const stripe = createStripeClient(data.environment);
      const customerId = await resolveOrCreateCustomer(stripe, { email, userId });

      const priceId = shopPriceIdFromSlug(product.slug);
      const prices = await stripe.prices.list({ lookup_keys: [priceId] });
      if (!prices.data.length) throw new Error("Product price is not configured in Stripe");
      const stripePrice = prices.data[0];

      // Member pricing is resolved server-side from the verified membership —
      // the client never supplies an amount.
      const pricing = resolvePrice(product as any, ownership.membership);

      const params: Stripe.Checkout.SessionCreateParams = {
        line_items: [
          pricing.memberDiscountApplied
            ? {
                price_data: {
                  currency: product.currency,
                  unit_amount: pricing.amountCents,
                  product: (typeof stripePrice.product === "string"
                    ? stripePrice.product
                    : stripePrice.product.id) as string,
                },
                quantity: 1,
              }
            : { price: stripePrice.id, quantity: 1 },
        ],
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer: customerId,
        payment_intent_data: { description: product.title },
        metadata: {
          product_slug: product.slug,
          product_title: product.title,
          price_id: priceId,
          member_price_applied: pricing.memberDiscountApplied ? "true" : "false",
          userId,
          ...stampConsentMetadata(data.consent),
        },
        allow_promotion_codes: true,
      };


      if (product.requires_shipping) {
        params.shipping_address_collection = {
          allowed_countries: ["US","CA","GB","AU","NZ","IE","FR","DE","ES","IT","NL","BE","SE","NO","DK","FI","CH","AT","PT","JP"],
        };
        params.shipping_options = [{
          shipping_rate_data: {
            type: "fixed_amount",
            display_name: "Standard shipping",
            fixed_amount: { amount: product.shipping_amount_cents ?? 0, currency: product.currency },
            delivery_estimate: {
              minimum: { unit: "business_day", value: 5 },
              maximum: { unit: "business_day", value: 14 },
            },
          },
        }];
        params.automatic_tax = { enabled: true };
      } else {
        // Digital: use Stripe full compliance handling.
        (params as unknown as { managed_payments: { enabled: boolean } }).managed_payments = { enabled: true };
      }

      const session = await stripe.checkout.sessions.create(params);
      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

// ---------------- Guest checkout (one-time products only) ----------------

export const createGuestShopCheckout = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        slug: z.string().min(1).max(200),
        email: z.string().trim().email().max(255),
        name: z.string().trim().min(1).max(120),
        returnUrl: z.string().url(),
        environment: StripeEnvSchema,
        consent: ConsentSchema,
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<CheckoutResult> => {
    try {
      const supabase = publicClient();
      const { data: product, error } = await supabase
        .from("shop_products")
        .select("slug, title, amount_cents, currency, active, external_purchase_url, requires_shipping, shipping_amount_cents, tax_code, kind")
        .eq("slug", data.slug)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!product || !product.active) throw new Error("Product not available");
      if (product.external_purchase_url) throw new Error("This product is sold on an external site");
      if (!product.amount_cents || product.amount_cents < 50) throw new Error("Product price is not configured");

      const stripe = createStripeClient(data.environment);
      const priceId = shopPriceIdFromSlug(product.slug);
      const prices = await stripe.prices.list({ lookup_keys: [priceId] });
      if (!prices.data.length) throw new Error("Product price is not configured in Stripe");
      const stripePrice = prices.data[0];

      const params: Stripe.Checkout.SessionCreateParams = {
        line_items: [{ price: stripePrice.id, quantity: 1 }],
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer_email: data.email,
        customer_creation: "always",
        payment_intent_data: { description: product.title },
        metadata: {
          product_slug: product.slug,
          product_title: product.title,
          price_id: priceId,
          guest_email: data.email,
          guest_name: data.name,
          ...stampConsentMetadata(data.consent),
        },
        allow_promotion_codes: true,
      };

      if (product.requires_shipping) {
        params.shipping_address_collection = {
          allowed_countries: ["US","CA","GB","AU","NZ","IE","FR","DE","ES","IT","NL","BE","SE","NO","DK","FI","CH","AT","PT","JP"],
        };
        params.shipping_options = [{
          shipping_rate_data: {
            type: "fixed_amount",
            display_name: "Standard shipping",
            fixed_amount: { amount: product.shipping_amount_cents ?? 0, currency: product.currency },
            delivery_estimate: {
              minimum: { unit: "business_day", value: 5 },
              maximum: { unit: "business_day", value: 14 },
            },
          },
        }];
        params.automatic_tax = { enabled: true };
      } else {
        (params as unknown as { managed_payments: { enabled: boolean } }).managed_payments = { enabled: true };
      }

      const session = await stripe.checkout.sessions.create(params);
      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

// ---------------- Membership checkout ----------------

const MembershipPriceIdSchema = z.enum(MEMBERSHIP_PRICE_IDS);

export const createMembershipCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        priceId: MembershipPriceIdSchema,
        returnUrl: z.string().url(),
        environment: StripeEnvSchema,
        consent: SubscriptionConsentSchema,
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<CheckoutResult> => {
    try {
      const { userId, claims } = context;
      const email = (claims as { email?: string } | undefined)?.email;
      const stripe = createStripeClient(data.environment);
      const customerId = await resolveOrCreateCustomer(stripe, { email, userId });
      const prices = await stripe.prices.list({ lookup_keys: [data.priceId] });
      if (!prices.data.length) throw new Error("Membership price is not configured in Stripe");
      const stripePrice = prices.data[0];
      const isRecurring = stripePrice.type === "recurring";
      const productId = typeof stripePrice.product === "string" ? stripePrice.product : stripePrice.product.id;
      const product = await stripe.products.retrieve(productId);
      const cadence = MEMBERSHIP_CADENCE[data.priceId];

      const consentMeta = stampConsentMetadata(data.consent);
      const params: Stripe.Checkout.SessionCreateParams = {
        line_items: [{ price: stripePrice.id, quantity: 1 }],
        mode: isRecurring ? "subscription" : "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer: customerId,
        metadata: {
          userId,
          membership_tier: data.priceId,
          product_slug: data.priceId,
          product_title: product.name,
          price_id: data.priceId,
          cadence,
          ...consentMeta,
        },
        ...(!isRecurring && { payment_intent_data: { description: product.name } }),
        ...(isRecurring && {
          subscription_data: {
            metadata: {
              userId,
              membership_tier: data.priceId,
              cadence,
              ...consentMeta,
            },
          },
        }),
      };

      (params as unknown as { managed_payments: { enabled: boolean } }).managed_payments = { enabled: true };

      const session = await stripe.checkout.sessions.create(params);
      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

export const createBillingPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        returnUrl: z.string().url(),
        environment: StripeEnvSchema,
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<PortalResult> => {
    try {
      const { data: subscription, error } = await (context.supabase.from("subscriptions") as any)
        .select("stripe_customer_id")
        .eq("user_id", context.userId)
        .eq("environment", data.environment)
        .neq("price_id", "lifetime_ascension_pass")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!subscription?.stripe_customer_id) throw new Error("No recurring membership found");
      const stripe = createStripeClient(data.environment);
      const portal = await stripe.billingPortal.sessions.create({
        customer: subscription.stripe_customer_id,
        return_url: data.returnUrl,
      });
      return { url: portal.url };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

// ---------------- Order history ----------------

export const listMyOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("orders")
      .select("id, order_number, product_slug, product_title, amount_cents, currency, status, environment, fulfillment_status, tracking_number, tracking_url, shipping_address, refunded_at, reward_points_awarded, created_at")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getOrderBySession = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ sessionId: z.string().min(4).max(200) }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: order } = await context.supabase
      .from("orders")
      .select("id, order_number, product_slug, product_title, amount_cents, currency, status, environment, fulfillment_status, shipping_address, reward_points_awarded, created_at")
      .eq("stripe_session_id", data.sessionId)
      .maybeSingle();
    return order;
  });

// NOTE: there is deliberately no unauthenticated guest-order lookup.
// A public `getGuestOrderBySession` previously read the service-role `orders`
// row for any Stripe session id and returned `guest_email` plus the unclaimed
// `guest_claim_token` — and the claim token is what binds an order to an
// account. It had no callers, so it was removed rather than left as a public
// PII/ownership endpoint. Guests receive their claim link by email from the
// verified Stripe webhook; signed-in buyers use `getOrderBySession`, which is
// scoped by RLS to their own orders.

export const claimGuestOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ claimToken: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Claim tokens are the only credential binding a guest order to an account,
    // so cap attempts per account to make token guessing/enumeration impractical.
    const { checkRateLimit } = await import("@/lib/rate-limit.server");
    const allowed = await checkRateLimit(`order-claim:${context.userId}`, 10, 3600);
    if (!allowed) return { error: "Too many claim attempts. Please try again later." };
    const { data: order, error } = await supabaseAdmin
      .from("orders")
      .select("id, user_id, product_slug, claimed_at")
      .eq("guest_claim_token", data.claimToken)
      .maybeSingle();
    if (error || !order) return { error: "Invalid or expired claim link" };
    if (order.user_id && order.user_id !== context.userId) return { error: "This order is claimed by another account" };
    const { error: updErr } = await (supabaseAdmin.from("orders") as any)
      .update({ user_id: context.userId, claimed_at: new Date().toISOString() })
      .eq("id", order.id);
    if (updErr) return { error: updErr.message };
    // Grant entitlement (digital only — physical requires shipping fulfillment)
    await supabaseAdmin.from("product_entitlements").upsert({
      user_id: context.userId,
      product_slug: order.product_slug,
      order_id: order.id,
    }, { onConflict: "user_id,product_slug,order_id" });
    return { ok: true, orderId: order.id };
  });

export const getAccountAutomation = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ environment: StripeEnvSchema }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const [rewards, referral, entitlements, subscription, recommendations, reviewRequests] = await Promise.all([
      (supabase.from("rewards_ledger") as any)
        .select("id, points, reason, description, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50),
      (supabase.from("referrals") as any)
        .select("code, referrer_reward_points, referee_reward_points, status, created_at")
        .eq("user_id", userId)
        .maybeSingle(),
      (supabase.from("product_entitlements") as any)
        .select("id, product_slug, order_id, granted_at, revoked_at, expires_at")
        .eq("user_id", userId)
        .is("revoked_at", null)
        .order("granted_at", { ascending: false }),
      // Fix: env-filter this read so sandbox rows don't shadow live status.
      (supabase.from("subscriptions") as any)
        .select("membership_tier, status, price_id, current_period_end, cancel_at_period_end, environment, cadence, updated_at")
        .eq("user_id", userId)
        .eq("environment", data.environment)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      (supabase.from("customer_recommendations") as any)
        .select("id, product_slug, reason, score, created_at")
        .eq("user_id", userId)
        .is("dismissed_at", null)
        .order("score", { ascending: false })
        .limit(6),
      (supabase.from("review_requests") as any)
        .select("id, product_slug, due_at, status")
        .eq("user_id", userId)
        .in("status", ["scheduled", "sent"])
        .order("due_at", { ascending: true })
        .limit(6),
    ]);

    const slugs = Array.from(new Set([
      ...((entitlements.data ?? []).map((row: { product_slug: string }) => row.product_slug)),
      ...((recommendations.data ?? []).map((row: { product_slug: string }) => row.product_slug)),
      ...((reviewRequests.data ?? []).map((row: { product_slug: string }) => row.product_slug)),
    ]));

    const { data: products } = slugs.length
      ? await supabase
          .from("shop_products")
          .select("slug, title, subtitle, image_url, amount_cents, currency, kind")
          .in("slug", slugs)
      : { data: [] };
    const productBySlug = new Map((products ?? []).map((product) => [product.slug, product]));
    const rewardRows = rewards.data ?? [];

    return {
      rewards: {
        balance: rewardRows.reduce((sum: number, row: { points: number }) => sum + row.points, 0),
        recent: rewardRows.slice(0, 8),
      },
      referral: referral.data ?? null,
      library: (entitlements.data ?? []).map((row: { product_slug: string }) => ({
        ...row,
        product: productBySlug.get(row.product_slug) ?? null,
      })),
      subscription: subscription.data ?? null,
      recommendations: (recommendations.data ?? []).map((row: { product_slug: string }) => ({
        ...row,
        product: productBySlug.get(row.product_slug) ?? null,
      })),
      reviewRequests: (reviewRequests.data ?? []).map((row: { product_slug: string }) => ({
        ...row,
        product: productBySlug.get(row.product_slug) ?? null,
      })),
    };
  });

// ---------------- Downloads ----------------

export type DownloadFile = { label: string; kind: string; format: string | null; sizeBytes: number | null; url: string };

export const getMyDownload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ orderId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }): Promise<{ url: string } | { error: string }> => {
    const res = await resolveDownloads(context.supabase, context.userId, data.orderId);
    if ("error" in res) return res;
    const first = res.files[0];
    if (!first) return { error: "No download is attached to this product yet." };
    return { url: first.url };
  });

export const listMyDownloads = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ orderId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }): Promise<{ files: DownloadFile[] } | { error: string }> => {
    return resolveDownloads(context.supabase, context.userId, data.orderId);
  });

async function resolveDownloads(
  supabase: any,
  userId: string,
  orderId: string,
): Promise<{ files: DownloadFile[] } | { error: string }> {
  // Canonical ownership check: use product_entitlements (honors revoked_at
  // + expires_at) so refunds and past_due revokes take effect immediately.
  const { data: entitlement } = await supabase
    .from("product_entitlements")
    .select("id, product_slug, order_id, revoked_at, expires_at")
    .eq("user_id", userId)
    .eq("order_id", orderId)
    .is("revoked_at", null)
    .maybeSingle();

  if (!entitlement) {
    // Beat-licence orders are owned through `licenses`, not
    // `product_entitlements`. Without this branch a paid licence purchase is
    // told it has no access to its own download.
    const { data: license } = await supabase
      .from("licenses")
      .select("id, user_id, beat_id, status, terms_snapshot")
      .eq("user_id", userId)
      .eq("order_id", orderId)
      .maybeSingle();
    if (license) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { resolveLicensedFiles } = await import("@/lib/bkls.server");
      const res = await resolveLicensedFiles(supabaseAdmin, license as any);
      if ("error" in res) return res;
      return { files: res.files.map((f) => ({ label: f.label, kind: f.kind, format: null, sizeBytes: null, url: f.url })) };
    }
    return { error: "You do not have access to this download." };
  }
  if (entitlement.expires_at && new Date(entitlement.expires_at).getTime() < Date.now()) {
    return { error: "Your access to this product has expired." };
  }


  const { data: product, error: prodErr } = await supabase
    .from("shop_products")
    .select("download_asset_path, title")
    .eq("slug", entitlement.product_slug)
    .maybeSingle();
  if (prodErr) return { error: prodErr.message };

  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  const { data: assets, error: assetsErr } = await (supabaseAdmin.from("product_assets") as any)
    .select("kind, label, storage_path, format, size_bytes, sort_order, created_at")
    .eq("product_slug", entitlement.product_slug)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (assetsErr) return { error: assetsErr.message };

  const rows: Array<{ kind: string; label: string; storage_path: string; format: string | null; size_bytes: number | null }> =
    assets && assets.length > 0
      ? assets
      : product?.download_asset_path
        ? [{ kind: "other", label: product.title ?? "Download", storage_path: product.download_asset_path, format: null, size_bytes: null }]
        : [];

  if (rows.length === 0) return { error: "No download is attached to this product yet." };

  const files: DownloadFile[] = [];
  for (const row of rows) {
    const { data: signed, error: signErr } = await supabaseAdmin.storage
      .from("downloads")
      .createSignedUrl(row.storage_path, 60 * 60, { download: row.label });
    if (signErr || !signed) return { error: signErr?.message ?? "Could not sign URL" };
    files.push({
      label: row.label,
      kind: row.kind,
      format: row.format,
      sizeBytes: row.size_bytes,
      url: signed.signedUrl,
    });
  }
  return { files };
}

// ---------------- Admin ----------------

export const listAllOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as never);
    const { data, error } = await context.supabase
      .from("orders")
      .select("id, product_slug, product_title, amount_cents, currency, status, environment, fulfillment_status, tracking_number, tracking_url, shipping_name, shipping_address, customer_email, guest_email, created_at, disputed_at")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const markOrderShipped = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        orderId: z.string().uuid(),
        trackingNumber: z.string().max(120).optional(),
        trackingUrl: z.string().url().max(500).optional(),
        fulfillmentStatus: z.enum(["unfulfilled","shipped","delivered"]).default("shipped"),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase
      .from("orders")
      .update({
        fulfillment_status: data.fulfillmentStatus,
        tracking_number: data.trackingNumber ?? null,
        tracking_url: data.trackingUrl ?? null,
      })
      .eq("id", data.orderId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
